import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MdFileDownload } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  AppCard,
  AppSelect,
  AnimatedSearchInput,
  OutlineButton,
  SortableTh,
  StatusBadge,
  TablePagination,
  TableLoader,
} from '../../../../components/ui';
import { indentStatusBadge } from '../../../../data/master/assetOwner';
import { INDENT_STATUS_OPTIONS } from '../../../../data/master/indents';
import { indentStatusToApi } from '../../../../models/indent.model';
import { listIndents, exportIndentsExcel } from '../../../../repositories/indent.repository';
import { usePagination } from '../../../../hooks/usePagination';
import { useTableSort } from '../../../../hooks/useTableSort';
import { formatIndianDateTimeParts } from '../../../../utils/datetime';
import { useAdminHeaderRefresh } from '../../../../contexts/AdminHeaderRefreshContext';
import { IndentFormModal } from './IndentFormModal';
import GroupedIndentsListPreview from './GroupedRequestUiPreview';
import { GROUPED_REQUEST_UI_PREVIEW } from '../../../../data/mock/groupedRequestUi';

const INDENT_SEARCH_HINTS = [
  'Search by request no.…',
  'Search by requester name…',
  'Search by catalog / type…',
  'Search by Business Unit…',
  'Search by site…',
  'Search by phone…',
  'Search for village…',
  'Search by status…',
];

function IndentDateChips({ value }) {
  const parts = formatIndianDateTimeParts(value);
  if (!parts.date && !parts.time) return '—';
  return (
    <Flex gap={1} wrap="nowrap">
      {parts.date ? (
        <StatusBadge status="neutral" fontSize="13px" px={2} py={0.5}>
          {parts.date}
        </StatusBadge>
      ) : null}
      {parts.time ? (
        <StatusBadge status="info" fontSize="13px" px={2} py={0.5}>
          {parts.time}
        </StatusBadge>
      ) : null}
    </Flex>
  );
}

export default function IndentsPage() {
  if (GROUPED_REQUEST_UI_PREVIEW) {
    return <GroupedIndentsListPreview />;
  }

  return <IndentsPageLive />;
}

function IndentsPageLive() {
  const navigate = useNavigate();
  const toast = useToast();
  const formModal = useDisclosure();
  const [indents, setIndents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const vendorDbId = vendorFilter?.startsWith('db:')
        ? Number(vendorFilter.slice(3))
        : null;
      const appUserId = vendorFilter?.startsWith('ag:')
        ? Number(vendorFilter.slice(3))
        : null;

      const indentRes = await listIndents({
        pageSize: 200,
        search: search.trim() || undefined,
        status: statusFilter ? indentStatusToApi(statusFilter) : undefined,
        vendorId: Number.isFinite(vendorDbId) ? vendorDbId : undefined,
        appUserId: Number.isFinite(appUserId) ? appUserId : undefined,
      });
      setIndents(indentRes.items || []);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load indents',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }, [toast, search, statusFilter, vendorFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useAdminHeaderRefresh(load);

  const vendorOptions = useMemo(() => {
    const map = new Map();
    indents.forEach((ind) => {
      const key =
        ind.vendorDbId != null
          ? `db:${ind.vendorDbId}`
          : ind.appUserId != null
            ? `ag:${ind.appUserId}`
            : ind.vendorId;
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, {
          value: key,
          label: ind.vendorName || ind.requesterName || key,
          description:
            ind.requesterSource === 'agency'
              ? 'Agency (mobile)'
              : ind.vendorId || 'Vendor',
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label)),
    );
  }, [indents]);

  const filtered = useMemo(() => {
    return indents.filter((ind) => {
      const matchV =
        !vendorFilter ||
        (vendorFilter.startsWith('db:') &&
          Number(vendorFilter.slice(3)) === Number(ind.vendorDbId)) ||
        (vendorFilter.startsWith('ag:') &&
          Number(vendorFilter.slice(3)) === Number(ind.appUserId)) ||
        ind.vendorId === vendorFilter;
      return matchV;
    });
  }, [indents, vendorFilter]);

  const { sorted, sort, toggleSort } = useTableSort(filtered, {
    requestNo: (i) => i.requestNo || i.indentId,
    createdAt: (i) => i.createdAt || '',
    vendor: (i) => i.vendorName || i.requesterName || i.vendorId,
    items: (i) => Number(i.itemCount || 0),
    summary: (i) => i.summary || '',
    status: (i) => i.status,
  });

  const {
    pageItems,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
  } = usePagination(sorted);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setVendorFilter('');
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const vendorDbId = vendorFilter?.startsWith('db:')
        ? Number(vendorFilter.slice(3))
        : null;
      const appUserId = vendorFilter?.startsWith('ag:')
        ? Number(vendorFilter.slice(3))
        : null;
      await exportIndentsExcel({
        search: search.trim() || undefined,
        status: statusFilter ? indentStatusToApi(statusFilter) : undefined,
        vendorId: Number.isFinite(vendorDbId) ? vendorDbId : undefined,
        appUserId: Number.isFinite(appUserId) ? appUserId : undefined,
      });
      toast({
        title: 'Export started',
        description: 'All indent requests matching current filters were included.',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Export failed',
        status: 'warning',
        position: 'top-right',
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <AppCard mb={4}>
        <Flex
          gap={3}
          align="end"
          wrap={{ base: 'wrap', xl: 'nowrap' }}
        >
          <Box flex="1.4" minW={{ base: '100%', sm: '220px' }}>
            <AnimatedSearchInput
              hints={INDENT_SEARCH_HINTS}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <Box flex="1" minW={{ base: '100%', sm: '160px' }}>
            <AppSelect
              label="Status"
              labelFontSize="13px"
              h="40px"
              value={statusFilter}
              onChange={(v) => setStatusFilter(v ?? '')}
              options={[
                { value: '', label: 'All statuses' },
                ...INDENT_STATUS_OPTIONS,
              ]}
            />
          </Box>
          <Box flex="1" minW={{ base: '100%', sm: '160px' }}>
            <AppSelect
              label="Requester"
              labelFontSize="13px"
              h="40px"
              isSearchable
              searchPlaceholder="Search requester…"
              value={vendorFilter}
              onChange={(v) => setVendorFilter(v ?? '')}
              options={[{ value: '', label: 'All requesters' }, ...vendorOptions]}
            />
          </Box>
          <Flex gap={2} align="end" flexShrink={0} ml={{ base: 0, xl: 'auto' }}>
            <OutlineButton
              size="sm"
              h="40px"
              minH="40px"
              minW="132px"
              px={4}
              onClick={clearFilters}
            >
              Clear filters
            </OutlineButton>
            <OutlineButton
              size="sm"
              h="40px"
              minH="40px"
              minW="132px"
              px={4}
              leftIcon={<MdFileDownload />}
              onClick={handleExport}
              isLoading={exporting}
              isDisabled={loading || exporting}
            >
              Export Excel
            </OutlineButton>
            {/* Indenter Form temporarily hidden
            {indenterFormEnabled ? (
              <PrimaryButton
                size="sm"
                h="40px"
                minH="40px"
                minW="132px"
                px={4}
                leftIcon={<MdAdd />}
                onClick={formModal.onOpen}
              >
                Indenter Form
              </PrimaryButton>
            ) : null}
            */}
          </Flex>
        </Flex>
      </AppCard>

      <AppCard p={0} overflow="hidden">
        {loading ? (
          <TableLoader rows={7} columns={5} />
        ) : (
          <>
            <Box overflowX="auto">
              <Table
                variant="simple"
                size="sm"
                sx={{
                  width: '100%',
                  th: { px: 2.5, py: 2.5, whiteSpace: 'nowrap', fontSize: '12px' },
                  td: {
                    px: 2.5,
                    py: 2.5,
                    verticalAlign: 'middle',
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <Thead bg="bg.input">
                  <Tr>
                    <SortableTh columnKey="requestNo" sort={sort} onSort={toggleSort}>
                      Request
                    </SortableTh>
                    <SortableTh columnKey="createdAt" sort={sort} onSort={toggleSort}>
                      Requested date & time
                    </SortableTh>
                    <SortableTh columnKey="vendor" sort={sort} onSort={toggleSort}>
                      Requester
                    </SortableTh>
                    <SortableTh columnKey="items" sort={sort} onSort={toggleSort}>
                      Items
                    </SortableTh>
                    <SortableTh columnKey="summary" sort={sort} onSort={toggleSort}>
                      Summary
                    </SortableTh>
                    <SortableTh columnKey="status" sort={sort} onSort={toggleSort}>
                      Status
                    </SortableTh>
                  </Tr>
                </Thead>
                <Tbody>
                  {pageItems.map((ind) => {
                    const summary = ind.assignmentSummary;
                    const assignedHover =
                      ind.status === 'Assigned' && summary
                        ? [
                            summary.ownerType,
                            summary.ownerName,
                            summary.mobile,
                            summary.regNo,
                            summary.assignedByActor === 'agency'
                              ? `Given by requester${
                                  summary.assignedByLabel
                                    ? ` · ${summary.assignedByLabel}`
                                    : ''
                                }`
                              : summary.assignedByActor === 'staff'
                                ? `Assigned by team${
                                    summary.assignedByLabel
                                      ? ` · ${summary.assignedByLabel}`
                                      : ''
                                  }`
                                : summary.assignedByLabel
                                  ? `by ${summary.assignedByLabel}`
                                  : '',
                          ]
                            .filter(Boolean)
                            .join(' · ')
                        : '';
                    return (
                      <Tr
                        key={ind.id || ind.requestNo}
                        cursor="pointer"
                        _hover={{ bg: 'bg.input' }}
                        onClick={() =>
                          navigate(
                            `/admin/equipments/indents/${ind.id || ind.requestNo}`,
                          )
                        }
                      >
                        <Td fontFamily="mono" fontWeight="700" fontSize="15px">
                          {ind.requestNo || ind.indentId}
                        </Td>
                        <Td>
                          <IndentDateChips value={ind.createdAt} />
                        </Td>
                        <Td>
                          <Text fontWeight="600" fontSize="15px">
                            {ind.requesterName || ind.vendorName || '—'}
                          </Text>
                          <Text fontSize="13px" color="text.secondary">
                            {ind.agencyTradeName ||
                              (ind.requesterSource === 'agency'
                                ? 'Agency (mobile)'
                                : ind.vendorId || 'Vendor')}
                          </Text>
                        </Td>
                        <Td fontWeight="600" fontSize="15px">
                          {ind.itemCount ?? ind.lines?.length ?? 0}
                        </Td>
                        <Td fontSize="14px" whiteSpace="normal" minW="180px">
                          {ind.summary || '—'}
                        </Td>
                        <Td onClick={(e) => e.stopPropagation()}>
                          {assignedHover ||
                          ind.status === 'Withdrawn' ||
                          ind.status === 'Cancelled' ? (
                            <Tooltip
                              hasArrow
                              placement="top"
                              label={
                                assignedHover ||
                                (String(ind.notes || '').trim()
                                  ? String(ind.notes).trim()
                                  : ind.status === 'Withdrawn'
                                    ? 'Buyer withdrew — no longer required'
                                    : 'Request cancelled')
                              }
                            >
                              <Box as="span" display="inline-block">
                                <StatusBadge status={indentStatusBadge(ind.status)}>
                                  {ind.status}
                                </StatusBadge>
                              </Box>
                            </Tooltip>
                          ) : (
                            <StatusBadge status={indentStatusBadge(ind.status)}>
                              {ind.status}
                            </StatusBadge>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                  {pageItems.length === 0 ? (
                    <Tr>
                      <Td colSpan={6}>
                        <Text color="text.secondary" py={10} textAlign="center">
                          No requests match these filters.
                        </Text>
                      </Td>
                    </Tr>
                  ) : null}
                </Tbody>
              </Table>
            </Box>
            <TablePagination
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </AppCard>

      <IndentFormModal
        isOpen={formModal.isOpen}
        onClose={formModal.onClose}
        onSaved={(indent) => {
          formModal.onClose();
          load();
          if (indent?.id || indent?.requestNo) {
            navigate(`/admin/equipments/indents/${indent.id || indent.requestNo}`);
          }
        }}
      />
    </Box>
  );
}
